import styled, { css, keyframes } from 'styled-components';
import { motion } from 'framer-motion';

import { breakpoints } from 'theme';

const primary = '#00696b';
const primaryLight = '#00a4a6';
const onSurface = '#191c1e';
const onVariant = '#3d4949';
const muted = '#64748b';
const border = '#e2e8f0';
const cardShadow = '0 8px 32px rgba(0, 50, 51, 0.12)';

const planGradient = css`
  background: linear-gradient(135deg, ${primary}, ${primaryLight});
`;

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const arrowPulse = keyframes`
  0%,
  100% {
    transform: translateX(0);
    opacity: 0.85;
  }
  50% {
    transform: translateX(4px);
    opacity: 1;
  }
`;

const arrowPulseDown = keyframes`
  0%,
  100% {
    transform: rotate(90deg) translateX(0);
    opacity: 0.85;
  }
  50% {
    transform: rotate(90deg) translateX(4px);
    opacity: 1;
  }
`;

const highlightGlow = keyframes`
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(0, 164, 166, 0);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(0, 164, 166, 0.12);
  }
`;

const staggerReveal = (delay: string) => css`
  animation: ${fadeUp} 0.42s cubic-bezier(0.22, 1, 0.36, 1) ${delay} both;
`;

export const ModalPanel = styled(motion.div)`
  position: relative;
  width: min(640px, calc(100vw - 32px));
  max-height: calc(100svh - 48px);
  overflow: auto;
  background: #fff;
  border-radius: 16px;
  box-shadow: ${cardShadow};
  padding: 32px 32px 28px;

  @media ${breakpoints.downSmPlus} {
    padding: 24px 20px 20px;
    border-radius: 12px;
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: ${muted};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  svg {
    width: 20px;
    height: 20px;
    pointer-events: none;
  }

  &:hover:not(:disabled) {
    background: #f8fafc;
    color: ${onSurface};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const ModalHeader = styled.header`
  padding-right: 40px;
  margin-bottom: 24px;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.25;
  color: ${onSurface};

  @media ${breakpoints.downSmPlus} {
    font-size: 18px;
  }
`;

export const ModalSubtitle = styled.p`
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.5;
  font-weight: 500;
  color: ${muted};
  text-wrap: pretty;
`;

export const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  animation: ${fadeUp} 0.32s cubic-bezier(0.22, 1, 0.36, 1) both;
`;

export const ModeSwitchSection = styled.section`
  ${staggerReveal('0.06s')}
  margin-bottom: 24px;
`;

export const ModeSwitchRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 14px;

  @media ${breakpoints.downSmPlus} {
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }
`;

export const ModeCard = styled.div<{ $highlight?: boolean }>`
  flex: 1;
  min-width: 0;
  min-height: 160px;
  padding: 26px 20px;
  border-radius: 14px;
  border: 2px solid ${({ $highlight }) => ($highlight ? primaryLight : border)};
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(0, 164, 166, 0.06)' : '#f8fafc'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;

  ${({ $highlight }) =>
    $highlight
      ? css`
          animation: ${highlightGlow} 2.4s ease-in-out 0.5s infinite;
        `
      : ''}

  @media ${breakpoints.downSmPlus} {
    width: 100%;
    min-height: 136px;
    padding: 22px 16px;
  }
`;

export const ModeIconBadge = styled.div<{ $emphasis?: boolean }>`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid
    ${({ $emphasis }) => ($emphasis ? 'rgba(0, 164, 166, 0.28)' : border)};
  box-shadow: ${({ $emphasis }) =>
    $emphasis
      ? '0 4px 14px rgba(0, 164, 166, 0.14)'
      : '0 2px 8px rgba(15, 23, 42, 0.06)'};
  color: ${({ $emphasis }) => ($emphasis ? primary : muted)};

  > svg {
    width: 26px;
    height: 26px;
    flex-shrink: 0;
  }
`;

export const ModeIconGraphic = styled.div`
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  svg {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const ModeCardLabel = styled.span`
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${muted};
`;

export const ModeCardValue = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: ${onSurface};
  line-height: 1.35;
  word-break: break-word;
`;

export const ModeArrow = styled.div`
  flex-shrink: 0;
  align-self: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: ${primary};
  background: rgba(0, 164, 166, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${arrowPulse} 1.8s ease-in-out infinite;

  svg {
    width: 20px;
    height: 20px;
  }

  @media ${breakpoints.downSmPlus} {
    animation: ${arrowPulseDown} 1.8s ease-in-out infinite;
  }
`;

export const InfoBannerStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const InfoBanner = styled.div<{ $tone?: 'timing' | 'neutral' }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${({ $tone }) =>
    $tone === 'timing' ? 'rgba(0, 164, 166, 0.06)' : '#f8fafc'};
  border: 1px solid
    ${({ $tone }) => ($tone === 'timing' ? 'rgba(0, 164, 166, 0.2)' : border)};

  &:nth-child(1) {
    ${staggerReveal('0.12s')}
  }

  &:nth-child(2) {
    ${staggerReveal('0.18s')}
  }
`;

export const InfoBannerIcon = styled.div<{ $tone?: 'timing' | 'neutral' }>`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $tone }) => ($tone === 'timing' ? primary : muted)};
  background: ${({ $tone }) =>
    $tone === 'timing' ? 'rgba(0, 164, 166, 0.12)' : '#fff'};
  border: 1px solid
    ${({ $tone }) => ($tone === 'timing' ? 'rgba(0, 164, 166, 0.18)' : border)};

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const InfoBannerContent = styled.div`
  min-width: 0;
  flex: 1;
`;

export const InfoBannerLabel = styled.span<{ $tone?: 'timing' | 'neutral' }>`
  display: block;
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${({ $tone }) => ($tone === 'timing' ? primary : muted)};
`;

export const InfoBannerText = styled.p<{ $preLine?: boolean }>`
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  font-weight: 500;
  color: ${onSurface};
  text-wrap: pretty;
  white-space: ${({ $preLine }) => ($preLine ? 'pre-line' : 'normal')};
`;

export const InfoBannerDetail = styled.p`
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.5;
  font-weight: 500;
  color: ${onVariant};
  text-wrap: pretty;
`;

export const AddCardPanel = styled.section`
  border: 1px solid ${border};
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  ${staggerReveal('0.06s')}
`;

export const AddCardStatus = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 18px;
  background: rgba(0, 164, 166, 0.06);
  border-bottom: 1px solid rgba(0, 164, 166, 0.14);
`;

export const AddCardStatusIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${primary};
  background: rgba(0, 164, 166, 0.12);
  border: 1px solid rgba(0, 164, 166, 0.2);

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const AddCardStatusContent = styled.div`
  min-width: 0;
  flex: 1;
`;

export const AddCardStatusLabel = styled.span`
  display: block;
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${primary};
`;

export const AddCardStatusText = styled.p<{ $preLine?: boolean }>`
  margin: 0;
  font-size: 14px;
  line-height: 1.55;
  font-weight: 500;
  color: ${onSurface};
  text-wrap: pretty;
  white-space: ${({ $preLine }) => ($preLine ? 'pre-line' : 'normal')};
`;

export const AddCardNextSection = styled.div`
  padding: 16px 18px 18px;
`;

export const AddCardNextLabel = styled.span`
  display: block;
  margin-bottom: 12px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${muted};
`;

export const AddCardDetailList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const AddCardDetailItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

export const AddCardDetailIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${primary};
  background: rgba(0, 164, 166, 0.08);
  border: 1px solid rgba(0, 164, 166, 0.16);

  svg {
    width: 17px;
    height: 17px;
  }
`;

export const AddCardDetailText = styled.p`
  margin: 2px 0 0;
  font-size: 14px;
  line-height: 1.55;
  font-weight: 500;
  color: ${onVariant};
  text-wrap: pretty;
`;

export const ModalFooter = styled.footer`
  margin-top: 28px;
  padding-top: 24px;
  border-top: 1px solid ${border};
`;

export const ModalActions = styled.div`
  display: flex;
  gap: 12px;

  @media ${breakpoints.downSmPlus} {
    flex-direction: column-reverse;
  }
`;

export const ModalBtnSecondary = styled.button`
  flex: 1;
  padding: 12px 20px;
  border-radius: 12px;
  border: 2px solid ${border};
  background: #fff;
  color: ${onVariant};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    background: #f8fafc;
    border-color: #cbd5e1;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const ModalBtnPrimary = styled.button`
  flex: 1.2;
  padding: 12px 20px;
  border-radius: 12px;
  border: none;
  ${planGradient}
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  transition: opacity 0.15s ease, transform 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.92;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

export const ModalError = styled.p`
  margin: 0 0 16px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(255, 90, 90, 0.08);
  border: 1px solid rgba(255, 90, 90, 0.25);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
  color: ${({ theme }) => theme.color.bittersweet};
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.88);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
  animation: ${fadeUp} 0.2s ease-out both;
`;

export const LoaderSlot = styled.div`
  position: relative;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LoadingText = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${onVariant};
  text-wrap: pretty;
`;

export const ContentArea = styled.div`
  position: relative;
`;
